<template>
  <section class="pricing-plans">
    <div class="container">
      <div
        class="plan-card"
        v-for="plan in monthlyPlans"
        :key="plan.name"
        :class="plan.name"
      >
        <h2 class="plan-title">{{ plan.name }}</h2>
        <p class="plan-subtitle">{{ plan.description }}</p>
        <div class="plan-price-with-old-price" v-if="plan.old_price">
          <div class="price-wrap">
            <span class="price">{{ plan.price }}</span>
            <span v-if="plan.discount" class="discount-badge"
              >-{{ plan.discount }}%</span
            >
          </div>
          <span class="old-price">{{ plan.old_price }}</span>
          <span class="suffix">{{ formatPrice(plan) }}</span>
        </div>
        <div class="plan-price" v-else>
          <span class="price">{{ plan.price }}</span>
          <span class="suffix">{{ formatPrice(plan) }}</span>
        </div>
        <ul class="plan-features">
          <li
            v-for="f in buildFeatures(plan?.plan_features)"
            :key="f.key"
            :class="{ unavailable: !f.available }"
          >
            <div class="square">
              <img
                :src="
                  f.available
                    ? require('@/assets/images/icons/check.svg')
                    : require('@/assets/images/icons/cross-red.svg')
                "
                alt=""
              />
            </div>
            {{ f.text }}
          </li>
        </ul>
        <template v-if="shouldShowButton(plan.name)">
          <button class="plan-btn" @click="onPlanClick(plan)">
            {{ getButtonText(plan.name) }}
          </button>
        </template>
        <template v-else>
          <div class="plan-badge mt-auto">{{ getBadgeText(plan.name) }}</div>
        </template>
      </div>
    </div>
    <BuyASubscriptionModal ref="subscriptionModal" @pay="onModalPay" />
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import BuyASubscriptionModal from "@/components/Modals/BuyASubscriptionModal.vue";
import { useRouter } from "vue-router";

const isLoading = ref(true);
const isPaymentLoading = ref(false);
const store = useStore();
const loggedIn = computed(() => store.state.auth.status.loggedIn);
const plans = ref([]);
// const memberships = ref([]);
const subscriptionModal = ref(null);
const router = useRouter();

const monthlyPlans = computed(() =>
  plans.value.filter((p) => p.type === "monthly")
);

const userHasMembership = ref(false);
const membershipName = ref("");

const buildFeatures = (pf) => {
  if (!pf || typeof pf !== "object") return [];

  const features = [];

  // 1) Проверки в месяц
  if (pf.unlimited_checks_per_month) {
    features.push({
      key: "checks",
      available: true,
      text: "Безлимитные полные проверки в месяц",
    });
  } else {
    const n = Number(pf.full_checks_per_month || 0);
    features.push({
      key: "checks",
      available: n > 0,
      text:
        n > 0 ? `${n} полных проверок в месяц` : "Полные проверки недоступны",
    });
  }

  // 2) Покупки на B2B маркетплейсе
  if ("basket_enabled" in pf) {
    features.push({
      key: "basket",
      available: !!pf.basket_enabled,
      text: "Покупки на B2B маркетплейсе",
    });
  }

  // 3) Уведомления об изменениях
  if ("notifications_enabled" in pf) {
    features.push({
      key: "notifications",
      available: !!pf.notifications_enabled,
      text: "Уведомления об изменениях",
    });
  }

  // 4) Размещение/открытие товаров (витрина)
  if ("showcase_enabled" in pf || "can_open_product" in pf) {
    const available = !!pf.showcase_enabled || !!pf.can_open_product;
    features.push({
      key: "showcase",
      available,
      text: "Размещение и управление товарами",
    });
  }

  // 5) Мессенджер
  if ("messenger_enabled" in pf) {
    features.push({
      key: "messenger",
      available: !!pf.messenger_enabled,
      text: "Мессенджер",
    });
  }

  // 6) Друзья/контакты
  if ("friends_enabled" in pf) {
    features.push({
      key: "friends",
      available: !!pf.friends_enabled,
      text: "Список партнёров/контактов",
    });
  }

  return features;
};

const shouldShowButton = (planName) => {
  const name = normalizePlanName(planName);
  if (!loggedIn.value) return true;
  if (loggedIn.value && name === "free") return false;
  if (isCurrentMembership(planName)) return false;
  return true;
};

const getBadgeText = (planName) => {
  if (isCurrentMembership(planName)) return "Ваш текущий тариф";
  if (loggedIn.value && normalizePlanName(planName) === "free")
    return "Вы уже в системе";
  return "";
};

const normalizePlanName = (n) =>
  (n || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(" на год", "")
    .replace("pro", "premium");

const isCurrentMembership = (planName) =>
  userHasMembership.value &&
  normalizePlanName(planName) === normalizePlanName(membershipName.value);

const findYearlyPair = (plan) => {
  return plans.value.find(
    (p) =>
      p.type === "yearly" &&
      normalizePlanName(p.name) === normalizePlanName(plan.name)
  );
};

const onPlanClick = (plan) => {
  const yearlyPlan = findYearlyPair(plan);

  if (plan.name === "Free") {
    router.push("/login");
    return;
  }

  subscriptionModal.value?.openModal({
    monthlyPlan: plan,
    yearlyPlan,
  });
};

const onModalPay = (payload) => {
  const { plan, auto_payment } = payload;
  if (!plan?.id) return;
  goToPayment({ plan: plan.id, auto_payment });
};

const goToPayment = (plan) => {
  isPaymentLoading.value = true;
  if (loggedIn.value) {
    store.dispatch("payments/createOrder", plan).then((res) => {
      window.location.href = res.order_url;
      isPaymentLoading.value = false;
    });
  } else {
    isPaymentLoading.value = false;
    router.push("/login");
  }
};

const formatPrice = (plan) => {
  if (!plan) return "";

  if (plan.name === "Free") {
    return "₽";
  }

  if (plan.type === "monthly") {
    return "₽ / месяц";
  }

  if (plan.type === "yearly") {
    return "₽";
  }

  return "₽";
};

const getButtonText = (planName) => {
  if (planName == "Free") return !loggedIn.value ? "Зарегистрироваться" : "";
  else if (planName == "Business" || planName == "Business на год")
    return "Начать сейчас";
  else if (planName == "Premium" || planName == "Premium на год")
    return "Подключить Premium";
};

onMounted(async () => {
  try {
    const me = await store.dispatch("user/me");
    userHasMembership.value = !!me?.membership_exists;
    membershipName.value = me?.name_membership || "";
  } catch (e) {
    console.warn("user/me failed", e);
  }

  // Получение списка тарифов
  store.dispatch("payments/getPlans").then((res) => {
    plans.value = res;
    // getPlans();
  });
});
</script>

<style scoped lang="scss">
.pricing-plans {
  padding: 60px 20px;
  color: rgba(62, 62, 62, 1);

  .container {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 32px;
  }

  .plan-card {
    background: white;
    border-radius: 30px;
    padding: 32px;
    width: 340px;
    display: flex;
    flex-direction: column;
    box-shadow: 6px 8px 24px 0px rgba(45, 46, 45, 0.2);

    &.Free {
      border: 1px solid rgba(0, 199, 130, 1);
    }

    &.Business {
      border: 1px solid rgba(0, 153, 255, 1);
    }

    &.Premium {
      border: 1px solid rgba(212, 175, 55, 1);
    }

    &.Free .plan-title {
      background: linear-gradient(180deg, #00c782 0%, #00613f 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    &.Business .plan-title {
      background: linear-gradient(90deg, #0099ff 0%, #005c99 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    &.Premium .plan-title {
      background: linear-gradient(180deg, #d4af37 0%, #6e5b1d 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .plan-title {
      font-size: 42px;
      font-weight: 700;
      margin-bottom: 8px;
    }

    .plan-subtitle {
      font-size: 16px;
      color: #666;
      margin-bottom: 24px;
    }

    .plan-price {
      font-size: 36px;
      font-weight: bold;
      margin-bottom: 24px;
      display: flex;
      align-items: center;
      gap: 8px;

      .price {
        font-size: 70px;
      }
      .suffix {
        font-size: 18px;
        font-weight: 700;
        display: block;
        margin-top: 4px;
      }
    }

    .plan-price-with-old-price {
      padding-top: 20px;
      padding-bottom: 50px;
      font-weight: bold !important;
      display: flex;
      gap: 12px;
      align-items: center;

      .price-wrap {
        position: relative;
      }

      .discount-badge {
        position: absolute;
        padding-left: 100px;
        top: -18px;
        background: #e74c3c;
        color: #fff;
        font-size: 14px;
        line-height: 1;
        padding: 6px 10px;
        border-radius: 5px;
        white-space: nowrap;
      }

      .price {
        font-size: 40px;
      }

      .old-price {
        font-size: 30px;
        color: #8f8f8f;
        position: relative;

        &::after {
          content: "";
          position: absolute;
          left: -2px;
          right: -2px;
          top: 50%;
          height: 2px;
          background: currentColor;
          transform: translateY(-50%);
          opacity: 0.9;
        }
      }
      .suffix {
        font-size: 18px;
      }
    }

    .plan-features {
      list-style: none;
      padding: 0;
      margin: 0 0 24px 0;
      width: 100%;
      text-align: left;

      li {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 16px;
        margin-bottom: 12px;

        .square {
          background: linear-gradient(90deg, #00c782 0%, #12986a 100%);
          width: 20px;
          min-width: 20px;
          height: 20px;
          border-radius: 5px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        // img {
        //   width: 13px;
        //   height: 13px;
        // }

        &.unavailable {
          color: #999;
        }
      }
    }

    .plan-btn {
      margin-top: auto;
      padding: 12px 24px;
      border-radius: 10px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      border: none;
    }

    &.Free .plan-btn {
      background: linear-gradient(90deg, #00c782 0%, #00613f 100%);
      color: #fff;
    }

    &.Business .plan-btn {
      background: linear-gradient(90deg, #0099ff 0%, #005c99 100%);
      color: #fff;
    }

    &.Premium .plan-btn {
      background: linear-gradient(90deg, #d4af37 0%, #6e5b1d 100%);
      color: #fff;
    }
  }
}
</style>
